import { Autorenew, CloudDoneOutlined, ErrorOutline } from '@mui/icons-material';
import { Popover, Tooltip } from '@mui/material';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { useEffect } from 'react';
import * as appStyles from '~/app.css';
import * as headerStyles from '~/components/Header/Header.css';
import { tooltipSlotProps, tooltipSlots } from '~/elements/Tooltip';
import { LEGACY, tooltipEnterDelay } from '~/utils/constants';
import { useParams } from '~/utils/hooks/hooks';
import registerComponent from '~/utils/registerComponent';
import { useStore, useTheme } from '~/utils/store';
import { rem } from '~/utils/utils';

const beforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();

export const SyncStatus = () => {
  const theme = useTheme();
  const status = useStore((state) => state.status);
  const popupState = usePopupState({ variant: 'popover' });
  const message = useStore((state) => state.message);
  const setStatus = useStore((state) => state.setStatus);
  const params = useParams();

  // Reset the error status on page navigation
  useEffect(() => {
    if (status === 'error') setStatus('idle');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.pageId]);

  // Unload warning if loading
  useEffect(() => {
    if (status === 'loading') {
      window.addEventListener('beforeunload', beforeUnload);
    } else {
      window.removeEventListener('beforeunload', beforeUnload);
    }
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [status]);

  return (
    <>
      {LEGACY && status === 'idle' ? null : (
        <Tooltip arrow title="See sync status" slots={tooltipSlots} slotProps={tooltipSlotProps} {...tooltipEnterDelay}>
          <button
            type="button"
            {...bindTrigger(popupState)}
            className={headerStyles.buttonCircular}
            style={{
              width: 34,
              height: 34,
            }}
          >
            {status === 'idle' && <CloudDoneOutlined style={{ fontSize: 20 }} />}
            {status === 'loading' && <Autorenew className={appStyles.rotate} style={{ fontSize: 19 }} />}
            {status === 'error' && <ErrorOutline style={{ fontSize: 20 }} />}
          </button>
        </Tooltip>
      )}
      {LEGACY && (status === 'idle' || status === 'loading') ? null : (
        <Popover
          {...bindMenu(popupState)}
          transitionDuration={{
            enter: 200,
            exit: 0,
          }}
          slotProps={{
            paper: {
              style: {
                maxWidth: 300,
                marginTop: '1rem',
                marginLeft: 2,
                borderRadius: 6,
                color: 'inherit',
                boxShadow: theme === 'light' ? 'rgba(0, 0, 0, 0.2) 2px 2px 8px 0' : undefined,
                backgroundColor: 'var(--root-bg-color)',
              },
            },
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <div
            style={{
              padding: `${rem(14)} ${rem(20)}`,
              backgroundColor: theme === 'light' ? '#F5F6FA' : '#262626',
              color: theme === 'light' ? '#5B76F5' : '',
              display: 'flex',
              alignItems: 'center',
              gap: rem(12),
            }}
          >
            {status &&
              {
                idle: <CloudDoneOutlined />,
                error: <ErrorOutline style={{ fontSize: 22 }} />,
                loading: <Autorenew style={{ fontSize: 22 }} />,
              }[status]}
            <h4
              style={{
                margin: 0,
                lineHeight: 1,
                marginTop: -1,
                fontFamily: 'var(--font-title)',
                fontWeight: 700,
                color: LEGACY ? 'inherit' : undefined,
              }}
            >
              {status &&
                {
                  idle: 'All changes saved to Drive',
                  error: 'Error saving changes to Drive',
                  loading: 'Saving...',
                }[status]}
            </h4>
          </div>
          <div
            style={{
              padding: `${rem(18)} ${rem(20)}`,
              lineHeight: 1.6,
              marginTop: -5,
              fontFamily: 'var(--font-title)',
              fontSize: 15,
              color: theme === 'light' ? '#3d3d3d' : '#fff',
            }}
          >
            {status &&
              {
                idle: 'Changes to have been saved to Drive.',
                error: message ?? 'There was an error saving changes to Drive.',
                loading: 'Saving changes to Drive...',
              }[status]}
          </div>
        </Popover>
      )}
    </>
  );
};

if (LEGACY) {
  registerComponent(SyncStatus, 'wc-sync-status', {
    wiki: 'json',
    wikis: 'json',
    files: 'json',
    user: 'json',
  });
}
